<template>
    <hts-drawer
        v-model="visible"
        :rules="rules"
        :submit="validate"
        :title="$t('labels.invitePartner')"
        @close="closeOverSide">
        <div class="flex flex-col justify-between gap-3">
            <div class="flex justify-between gap-2">
                <p class="w-full">
                    Tên công ty <span class="text-error-500">
                        *
                    </span>
                </p>
                <p class="w-full">
                    Email tham gia hệ thống <span class="text-error-500">
                        *
                    </span>
                </p>
                <p class="w-[80px] min-w-[80px]" />
            </div>
            <div
                v-for="(data, index) in dataInvites"
                :key="data.id"
                class="flex justify-between gap-2">
                <hts-select-cooperator
                    v-model="data.partnerId"
                    v-model:name="data.partnerName"
                    class="w-full"
                    required
                    name-select="partner"
                    type-cooperator="PARTNER"
                    label=""
                    key-label="abbreviationName"
                    not-yet-join-system
                    :color="data.partnerError ? 'error' : null"
                    :prop="`partner-${data.id}`"
                    @change="(partner) => changePartner(partner, index)">
                    <template #default="{ item }">
                        <p class="align-middle">
                            {{ item.abbreviationName || $t('labels.invalidData') }}
                        </p>
                        <div v-if="item.type.includes('PARTNER')">
                            <p
                                v-if="!item.sentInviteJoinSystem"
                                class="align-middle font-light italic">
                                Chưa mời
                            </p>
                            <p
                                v-if="item.sentInviteJoinSystem && !item.invitationStatus"
                                class="align-middle font-light italic">
                                Chờ xác nhận
                            </p>
                        </div>
                    </template>
                </hts-select-cooperator>

                <hts-form-input
                    v-model="data.email"
                    class="w-full"
                    label=""
                    required
                    name="email"
                    :prop="`email-${data.id}`"
                    :color="data.emailError ? 'error' : 'default'"
                    @change="changeEmail(index)" />

                <hts-form-button
                    class="mt-3 w-[80px] min-w-[80px]"
                    icon-name="hi-x-circle-icon"
                    icon-position="right"
                    color="error"
                    size="mini"
                    @click="deleteInvate(index)">
                    {{ $t('labels.delete') }}
                </hts-form-button>
            </div>
        </div>
        <hts-form-button
            class="mt-3"
            icon-name="hi-plus-icon"
            icon-position="right"
            color="primary"
            size="mini"
            @click="addInvite">
            {{ $t('labels.add') }}
        </hts-form-button>

        <hts-modal
            :show="showPopupConfirm"
            type="confirm"
            content="Hệ thống sẽ tự động gửi email mời tham gia hệ thống tới đối tác. Bạn có chắc chắn muốn tiếp tục."
            :loading="submiting"
            @close="showPopupConfirm = false"
            @confirm="submit" />
    </hts-drawer>
</template>

<script setup lang="ts">
const { $formatters } = useNuxtApp()

const { $t } = useNuxtApp()
const store = $store()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  }
})

const emits = defineEmits(['close', 'refreshTables', 'newPartner'])

const visible = ref(false)
const submiting = ref(false)
const showPopupConfirm = ref(false)

const dataInvites = ref<{ [key: string]: any }[]>([
  {
    id: $formatters.generateId(),
    partnerId: '',
    partnerName: '',
    email: ''
  }
])

const rules = computed(() => {
  const results = {} as { [key: string]: string }

  dataInvites.value.forEach((item: any) => {
    results[`partner-${item.id}`] = 'required'
    results[`email-${item.id}`] = 'required|regexEmail'
  })

  return results
})

const open = () => {
  visible.value = true
}

const addInvite = () => {
  dataInvites.value.push({
    id: $formatters.generateId(),
    partnerId: '',
    partnerName: '',
    email: ''
  })
}

const deleteInvate = (index: number) => {
  if (index < 0 || index >= dataInvites.value.length) {
    return
  }
  dataInvites.value.splice(index, 1)
  if (!dataInvites.value || !Array.isArray(dataInvites.value) || !dataInvites.value.length) {
    dataInvites.value = [
      {
        id: $formatters.generateId(),
        partnerId: '',
        partnerName: '',
        email: ''
      }
    ]
  }
}

const changePartner = (partner: any, index: number) => {
  if (partner?.[0]?.invitationEmail && partner?.[0]?.sentInviteJoinSystem) {
    dataInvites.value[index].email = partner[0].invitationEmail
  } else {
    dataInvites.value[index].email = ''
  }

  dataInvites.value[index].partnerError = false
}

const changeEmail = (index: number) => {
  dataInvites.value[index].emailError = false
}

const undo = () => {
  dataInvites.value = [
    {
      id: $formatters.generateId(),
      partnerId: '',
      partnerName: '',
      email: ''
    }
  ]

  showPopupConfirm.value = false
}

const closeOverSide = () => {
  visible.value = false
  undo()
}

const validate = () => {
  submit({ validate: true })
}

const submit = $lodash.debounce((options: { [key: string]: any }) => {
  submiting.value = true

  const body: { [key: string]: any } = {
    dataInvites: dataInvites.value
  }

  if (options?.validate) {
    body.validate = true
  }

  $api($url.partner.invites, { body })
    .then((data) => {
      const resData: any = data?.data?.value

      if (resData?.success) {
        if (resData.data?.error) {
          $toast().error($t(resData.data.error))
          dataInvites.value = resData.data.dataInvites
          return
        }
        if (options?.validate) {
          showPopupConfirm.value = true
        } else {
          showPopupConfirm.value = false
          $toast().success('Mời đội xe thành công')
          emits('refreshTables')
          closeOverSide()
        }
      }

      submiting.value = false
    })
    .catch(() => {
      submiting.value = false
    })
}, 300)

defineExpose({
  open
})
</script>
